/**
 * Scroll the window to `top` with an eased glide that always lands.
 *
 * `scrollTo({ behavior: 'smooth' })` is the browser's animation, and the
 * browser abandons it whenever layout shifts mid-flight: returning to the
 * conversation after the shelf empties coincides with the next question
 * streaming into the card above the composer, and Chrome stopped the glide at
 * 67 px or 298 px instead of 0. Here every frame writes an exact position, so
 * a layout change cannot strand it.
 *
 * Write-only on purpose: the start position is read once and every frame's
 * position is computed from it. On an iPhone a scroll write reaches the screen
 * a few frames later and `scrollY` / `visualViewport.pageTop` read stale in
 * between, so a glide that re-reads where the page is chases its own echo and
 * the page shudders back and forth.
 *
 * The shopper wins: a wheel, touch or key press stops the glide where it is.
 * Reduced motion jumps straight there. Returns a cancel function.
 */
export function glideWindowScrollTo(top: number): () => void {
  const from = window.scrollY;
  const distance = top - from;
  if (Math.abs(distance) < 1) return () => {};
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    window.scrollTo({ top, behavior: 'instant' });
    return () => {};
  }

  // Longer trips take a little longer, within a range that still reads as one move.
  const durationMs = Math.min(600, Math.max(320, Math.abs(distance) * 0.6));
  const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;
  let frame = 0;
  let start: number | null = null;

  const stop = () => {
    cancelAnimationFrame(frame);
    window.removeEventListener('wheel', stop);
    window.removeEventListener('touchstart', stop);
    window.removeEventListener('keydown', stop);
  };
  const step = (now: number) => {
    if (start === null) start = now;
    const t = Math.min(1, (now - start) / durationMs);
    // `instant` explicitly: the document sets `scroll-behavior: smooth`, which
    // would otherwise turn every frame into an animation of its own.
    window.scrollTo({ top: Math.round(from + distance * easeOutCubic(t)), behavior: 'instant' });
    if (t < 1) frame = requestAnimationFrame(step);
    else stop();
  };

  window.addEventListener('wheel', stop, { passive: true });
  window.addEventListener('touchstart', stop, { passive: true });
  window.addEventListener('keydown', stop);
  frame = requestAnimationFrame(step);
  return stop;
}
